import { ReactNode } from 'react'
import { Badge, Box, HStack, Text } from '@chakra-ui/react'
import SvgIcon from 'components/SvgIcon'
import { DARK_PAGE_BG, DARK_TEXT_LIGHT } from 'constants/dark-tokens'
import {
  MOBILE_SUB_HEADER_BAR,
  MOBILE_SUB_HEADER_TAG,
  MOBILE_SUB_HEADER_TAG_TONES,
  MOBILE_SUB_HEADER_TITLE,
  MobileSubHeaderTagTone
} from 'constants/mobile-page-template'

/** A status the page is in, shown beside its name. Tone decides the colour. */
export interface MobileSubPageTag {
  label: string
  tone: MobileSubHeaderTagTone
}

interface IMobileSubPageHeaderProps {
  /** The page's own name — the tail of the breadcrumb the desktop header shows. */
  title: ReactNode
  /** Where the arrow leads: the parent page, not simply the previous history entry. */
  onBack: () => void
  tags?: MobileSubPageTag[]
  /** The page's own actions (a kebab, a toggle) pinned to the right of the bar. */
  actions?: ReactNode
}

/**
 * The header a RallyPD sub page wears on a phone.
 *
 * The desktop header is a breadcrumb trail plus a Manage pill, which a 402px row
 * cannot hold. So a sub page trades the trail for its single most useful step —
 * back to the parent — and the pill moves to `MobileManageTab` on the right edge.
 * It sits under the app topbar and above the page, alongside the bottom nav —
 * it names the page and offers the way back, it does not stand in for them.
 *
 * Sticky, so the way out stays reachable however far the page scrolls.
 */
const MobileSubPageHeader = ({ title, onBack, tags, actions }: IMobileSubPageHeaderProps) => {
  return (
    <HStack {...MOBILE_SUB_HEADER_BAR} spacing={0} position="sticky" top={0} zIndex={20} bg={DARK_PAGE_BG}>
      <Box
        as="button"
        type="button"
        aria-label="Back"
        onClick={onBack}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        width="24px"
        height="24px"
        padding={0}
        border="none"
        background="transparent"
        cursor="pointer"
        borderRadius="4px"
        _hover={{ background: 'rgba(255, 255, 255, 0.08)' }}
        _active={{ background: 'rgba(255, 255, 255, 0.12)' }}
        _focus={{ boxShadow: 'none', outline: 'none' }}
        _focusVisible={{ boxShadow: 'none', outline: 'none' }}
        sx={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <SvgIcon iconName="ic_outline-arrow-back-ios" size={16} color={DARK_TEXT_LIGHT} />
      </Box>

      <Text {...MOBILE_SUB_HEADER_TITLE} flex={1} minWidth={0} m={0} noOfLines={1}>
        {title}
      </Text>

      {tags?.map(tag => (
        <Badge key={tag.label} {...MOBILE_SUB_HEADER_TAG} {...MOBILE_SUB_HEADER_TAG_TONES[tag.tone]} flexShrink={0}>
          {tag.label}
        </Badge>
      ))}

      {actions}
    </HStack>
  )
}

export default MobileSubPageHeader
